import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Table, Tag, Button, Space, Row, Col,
  Select, Badge, Timeline, Modal, Spin, Alert, Input, message
} from 'antd';
import { EnvironmentOutlined, HistoryOutlined, AimOutlined, SearchOutlined } from '@ant-design/icons';

// ============================================================
// 腾讯地图配置
// 请在 .env.local 中设置 REACT_APP_TMAP_KEY=你的腾讯地图Key
// 申请地址：https://lbs.qq.com/dev/console/application/mine
// ============================================================
const TMAP_KEY = process.env.REACT_APP_TMAP_KEY || '';

declare global {
  interface Window { TMap: any; qq: any; }
}

// 设备定位数据
const locationData = [
  { key: '1', iccid: '898603192101007341', device: '割草机A001', lat: 22.5397, lng: 113.9465, address: '深圳市南山区科技园', locType: 'AGPS', inFence: true,  lastUpdate: '14:23:11', status: 'normal' },
  { key: '2', iccid: '898603192101007342', device: 'CPE-SZ-002',  lat: 23.1291, lng: 113.2644, address: '广州市天河区珠江新城', locType: '基站', inFence: true,  lastUpdate: '14:20:44', status: 'normal' },
  { key: '3', iccid: '898603192101007343', device: 'VR-BJ-003',   lat: 39.9908, lng: 116.3363, address: '北京市海淀区中关村',   locType: 'AGPS', inFence: false, lastUpdate: '14:18:33', status: 'alert'  },
  { key: '4', iccid: '898603192101007344', device: '摄像头-SH-004',lat: 31.2304, lng: 121.4737, address: '上海市浦东新区陆家嘴', locType: 'IP',   inFence: true,  lastUpdate: '14:15:20', status: 'normal' },
  { key: '5', iccid: '898603192101007345', device: '对讲机-CD-005',lat: 30.5728, lng: 104.0668, address: '成都市高新区天府软件园', locType: '基站', inFence: true, lastUpdate: '14:10:05', status: 'normal' },
  { key: '6', iccid: '898603192101007346', device: '割草机B002',  lat: 22.3193, lng: 114.1694, address: '香港特别行政区', locType: 'AGPS', inFence: false, lastUpdate: '13:58:22', status: 'alert' },
];

const trailPoints = [
  { time: '09:00', lat: 22.5397, lng: 113.9465, address: '深圳市南山区科技园北门' },
  { time: '10:30', lat: 22.5412, lng: 113.9388, address: '深圳市南山区高新园地铁站' },
  { time: '12:15', lat: 22.5628, lng: 113.9543, address: '深圳市南山区西丽水库附近' },
  { time: '14:00', lat: 22.6015, lng: 113.9028, address: '深圳市宝安区石岩镇' },
  { time: '16:30', lat: 22.5389, lng: 113.9471, address: '深圳市南山区科技园南门' },
];

// -------- 腾讯地图 Hook --------
const useTMap = (containerId: string, enabled: boolean) => {
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (!enabled || !TMAP_KEY) { setError('no_key'); return; }

    const scriptId = 'tmap-sdk';
    if (document.getElementById(scriptId)) {
      if (window.TMap) { setLoaded(true); }
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://map.qq.com/api/gljs?v=1.exp&key=${TMAP_KEY}`;
    script.onload  = () => setLoaded(true);
    script.onerror = () => setError('load_failed');
    document.head.appendChild(script);
  }, [enabled]);

  useEffect(() => {
    if (!loaded || !window.TMap) return;
    const container = document.getElementById(containerId);
    if (!container || mapRef.current) return;
    try {
      mapRef.current = new window.TMap.Map(container, {
        zoom: 5,
        center: new window.TMap.LatLng(35.86166, 104.195397),
      });
    } catch(e) { setError('init_failed'); }
  }, [loaded, containerId]);

  const addMarkers = (points: typeof locationData) => {
    if (!mapRef.current || !window.TMap) return;
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = points.map(p => {
      const marker = new window.TMap.MultiMarker({
        map: mapRef.current,
        geometries: [{
          id: p.key,
          position: new window.TMap.LatLng(p.lat, p.lng),
          properties: { title: p.device },
        }],
        styles: {
          default: new window.TMap.MarkerStyle({
            width: 32, height: 32,
            src: p.status === 'alert'
              ? 'https://mapapi.qq.com/web/lbs/javascriptGL/demo/img/markerDefault.png'
              : 'https://mapapi.qq.com/web/lbs/javascriptGL/demo/img/marker.png',
          }),
        },
      });
      return marker;
    });
  };

  const showTrail = (points: typeof trailPoints) => {
    if (!mapRef.current || !window.TMap) return;
    const path = points.map(p => new window.TMap.LatLng(p.lat, p.lng));
    new window.TMap.MultiPolyline({
      map: mapRef.current,
      geometries: [{ id: 'trail', paths: path }],
      styles: { default: new window.TMap.PolylineStyle({ color: '#1890ff', width: 4, borderWidth: 2 }) },
    });
    mapRef.current.fitBounds(window.TMap.LatLngBounds.fromLatLngs(path));
  };

  return { loaded, error, addMarkers, showTrail, map: mapRef };
};

// -------- 主组件 --------
const Location: React.FC = () => {
  const [trailVisible, setTrailVisible] = useState(false);
  const [currentDevice, setCurrentDevice] = useState<any>(null);
  const [mapEnabled]  = useState(!!TMAP_KEY);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const { loaded: mainLoaded, error: mainError, addMarkers } = useTMap('main-map', mapEnabled);
  const { loaded: trailLoaded, error: trailError, showTrail } = useTMap('trail-map', trailVisible);

  // 地图加载后打点
  useEffect(() => {
    if (mainLoaded) addMarkers(locationData);
  }, [mainLoaded]);

  // 轨迹地图加载后画线
  useEffect(() => {
    if (trailLoaded && trailVisible) {
      setTimeout(() => showTrail(trailPoints), 300);
    }
  }, [trailLoaded, trailVisible]);

  const filtered = locationData.filter(d => {
    const matchFilter = filter === 'all' || (filter === 'alert' && !d.inFence) || (filter === 'normal' && d.inFence);
    const matchSearch = !search || d.device.includes(search) || d.iccid.includes(search);
    return matchFilter && matchSearch;
  });

  const MapPlaceholder = ({ id, height = 320 }: { id: string; height?: number }) => (
    <div id={id} style={{
      height,
      background: 'linear-gradient(135deg, #e8f4f8, #b8e2f5)',
      borderRadius: 8,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: 8,
      border: '1px solid #d9d9d9',
      position: 'relative',
    }}>
      {mainError === 'no_key' ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <EnvironmentOutlined style={{ fontSize: 40, color: '#1890ff', marginBottom: 8 }} />
          <p style={{ color: '#666', margin: 0, fontWeight: 500 }}>腾讯地图 Demo 模式</p>
          <p style={{ color: '#999', fontSize: 12, margin: '4px 0 12px' }}>
            配置 REACT_APP_TMAP_KEY 后显示真实地图
          </p>
          {/* 模拟设备点位 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {locationData.map(d => (
              <div key={d.key} style={{
                padding: '4px 10px', borderRadius: 16,
                background: d.inFence ? 'rgba(82,196,26,0.15)' : 'rgba(255,77,79,0.15)',
                border: `1px solid ${d.inFence ? '#52c41a' : '#ff4d4f'}`,
                fontSize: 12, cursor: 'pointer', color: d.inFence ? '#389e0d' : '#cf1322',
              }}
                onClick={() => message.info(`${d.device}: ${d.address}`)}
              >
                📍 {d.device}
              </div>
            ))}
          </div>
        </div>
      ) : mainError ? (
        <Alert type="error" message="地图加载失败，请检查 Key 是否有效" showIcon />
      ) : (
        <Spin tip="地图加载中..." />
      )}
    </div>
  );

  return (
    <div style={{ padding: 16 }}>
      {/* 统计行 */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {[
          { label: '在线设备', value: 4, color: '#52c41a' },
          { label: '越界告警', value: 2, color: '#ff4d4f' },
          { label: '围栏总数', value: 8, color: '#1890ff' },
          { label: '今日定位次数', value: 1248, color: '#722ed1' },
        ].map((item, i) => (
          <Col span={6} key={i}>
            <Card bordered={false} style={{ borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: item.color }}>{item.value}</div>
              <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{item.label}</div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* 地图区域 */}
      <Card
        title={<span><EnvironmentOutlined /> 设备实时位置</span>}
        extra={
          <Space>
            <Select
              value={filter} onChange={setFilter} style={{ width: 110 }}
              options={[{ value: 'all', label: '全部设备' }, { value: 'alert', label: '越界告警' }, { value: 'normal', label: '围栏内' }]}
            />
            <Button icon={<AimOutlined />} type="primary" onClick={() => { if (mainLoaded) addMarkers(locationData); else message.info('地图未加载'); }}>
              刷新定位
            </Button>
          </Space>
        }
        bordered={false}
        style={{ borderRadius: 8, marginBottom: 16 }}
      >
        {mapEnabled && !mainError ? (
          <div style={{ position: 'relative' }}>
            <div id="main-map" style={{ height: 320, borderRadius: 8 }} />
            {!mainLoaded && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.8)', borderRadius: 8 }}>
                <Spin tip="地图加载中..." />
              </div>
            )}
          </div>
        ) : (
          <MapPlaceholder id="main-map-placeholder" height={320} />
        )}

        {!TMAP_KEY && (
          <Alert
            style={{ marginTop: 8 }}
            type="info"
            showIcon
            message="配置真实地图Key"
            description={
              <span>
                在项目根目录创建 <code>.env.local</code> 文件，添加：
                <code style={{ marginLeft: 8, background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>
                  REACT_APP_TMAP_KEY=你的腾讯地图WebGL Key
                </code>
                <br />申请地址：<a href="https://lbs.qq.com/dev/console/application/mine" target="_blank" rel="noreferrer">https://lbs.qq.com/dev/console</a>
              </span>
            }
          />
        )}
      </Card>

      {/* 设备列表 */}
      <Card
        title="设备定位列表"
        extra={
          <Input
            placeholder="搜索设备/ICCID"
            prefix={<SearchOutlined />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
        }
        bordered={false}
        style={{ borderRadius: 8 }}
      >
        <Table
          dataSource={filtered}
          size="small"
          pagination={{ pageSize: 10 }}
          columns={[
            { title: 'ICCID', dataIndex: 'iccid', key: 'iccid', width: 180, ellipsis: true },
            { title: '设备名称', dataIndex: 'device', key: 'device', width: 130 },
            { title: '纬度', dataIndex: 'lat', key: 'lat', width: 110 },
            { title: '经度', dataIndex: 'lng', key: 'lng', width: 110 },
            { title: '当前地址', dataIndex: 'address', key: 'address', ellipsis: true },
            { title: '定位方式', dataIndex: 'locType', key: 'locType', width: 80, render: v => <Tag color="blue">{v}</Tag> },
            {
              title: '围栏状态', dataIndex: 'inFence', key: 'inFence', width: 100,
              render: v => <Badge status={v ? 'success' : 'error'} text={v ? '围栏内' : '⚠️ 越界'} />,
            },
            { title: '最近更新', dataIndex: 'lastUpdate', key: 'lastUpdate', width: 100 },
            {
              title: '操作', key: 'action', width: 130, fixed: 'right' as const,
              render: (_: any, r: any) => (
                <Space>
                  <Button
                    size="small"
                    icon={<EnvironmentOutlined />}
                    onClick={() => message.info(`定位至：${r.address}`)}
                  >定位</Button>
                  <Button
                    size="small"
                    icon={<HistoryOutlined />}
                    onClick={() => { setCurrentDevice(r); setTrailVisible(true); }}
                  >轨迹</Button>
                </Space>
              ),
            },
          ]}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* 历史轨迹弹窗 */}
      <Modal
        title={<span><HistoryOutlined /> 历史轨迹 — {currentDevice?.device}</span>}
        open={trailVisible}
        onCancel={() => setTrailVisible(false)}
        footer={null}
        width={640}
        destroyOnClose
      >
        {mapEnabled && !trailError ? (
          <div style={{ position: 'relative', marginBottom: 16 }}>
            <div id="trail-map" style={{ height: 220, borderRadius: 8 }} />
            {!trailLoaded && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.8)', borderRadius: 8 }}>
                <Spin />
              </div>
            )}
          </div>
        ) : (
          <div style={{ height: 180, background: 'linear-gradient(135deg,#e8f4f8,#b8e2f5)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, border: '1px solid #d9d9d9' }}>
            <span style={{ color: '#999', fontSize: 13 }}>轨迹地图（配置 TMAP_KEY 后显示）</span>
          </div>
        )}
        <Timeline
          items={trailPoints.map((p, i) => ({
            color: i === 0 ? 'green' : i === trailPoints.length - 1 ? 'red' : 'blue',
            children: (
              <div>
                <strong>{p.time}</strong>
                <div style={{ color: '#555', fontSize: 13 }}>{p.address}</div>
                <div style={{ color: '#999', fontSize: 11 }}>{p.lat}, {p.lng}</div>
              </div>
            ),
          }))}
        />
      </Modal>
    </div>
  );
};

export default Location;
